/**
 * Help/FAQ Content Structure
 *
 * Centralized content for the Help page, separated from presentation.
 * Each section has a title, content, and optional metadata for search and linking.
 */

import { flags } from "@/config/featureFlags";
export interface HelpStep {
  title: string;
  description: string;
  link?: string;
}

export interface HelpSection {
  id: string;
  title: string;
  category: "getting-started" | "features" | "advanced";
  summary: string;
  requiredData?: string[];
  steps?: HelpStep[];
  content: string;
  tips?: string[];
  relatedLinks?: { label: string; path: string }[];
}

export const helpSections: HelpSection[] = [
  // =============================================================================
  // GETTING STARTED
  // =============================================================================
  {
    id: "onboard-client",
    title: "How do I onboard a new client?",
    category: "getting-started",
    summary:
      "Getting started with a new client involves three key steps: importing donors, creating a brand profile, and generating your first campaign.",
    requiredData: [
      "Donor CSV with contact info and giving history",
      "Organization mission statement and values",
      "Brand voice preferences (tone, key messages)",
    ],
    steps: [
      {
        title: "Import Donors",
        description:
          "Upload your donor database to enable segmentation and personalization. Navigate to Configuration > Data Import and upload a CSV or Excel file with donor records.",
        link: "config?tab=data",
      },
      {
        title: "Create Brand Profile",
        description:
          "Define your organization's voice and messaging guidelines. This powers AI-generated content that sounds authentically like your organization.",
        link: "brand-profile",
      },
      ...(flags.enableCampaignWizard
        ? [
            {
              title: "Generate First Campaign",
              description:
                "Launch the Campaign Builder to create your first AI-powered campaign. The wizard will guide you through channel selection, audience targeting, and content generation.",
              link: "build",
            },
          ]
        : []),
    ],
    content: `After completing these three steps, your client is fully onboarded and ready to run campaigns.

The onboarding checklist on the Overview page tracks your progress and provides quick links to each step.`,
    tips: [
      "Include giving history in your donor import for better segmentation results",
      "Take time on the brand profile - it significantly improves AI content quality",
      "Start with a simple campaign to learn the system before complex multi-channel efforts",
    ],
    relatedLinks: [
      { label: "View Onboarding Checklist", path: "" },
      { label: "Configuration", path: "settings" },
    ],
  },

  // =============================================================================
  // CAMPAIGNS
  // =============================================================================
  {
    id: "start-campaign",
    title: "Where do I start a campaign?",
    category: "features",
    summary:
      "Campaigns are created in the Campaign Builder, accessible from the Campaigns page or directly via the Build button.",
    requiredData: [
      "Campaign name and type (Appeal, Event, Program, etc.)",
      "Target channels (Direct Mail, Email, Social)",
      "Audience description or segment selection",
      "Goal statement",
    ],
    steps: [
      {
        title: "Open Campaign Builder",
        description:
          "Click '+ New Campaign' from the Campaigns page, or use the Build button in the navigation. This opens the full-page Campaign Builder wizard.",
        link: "campaigns",
      },
      {
        title: "Define Campaign Basics",
        description:
          "Enter your campaign name, type (Appeal, Event, Program, Endowment, Capital), season, and overall tone. Describe your target audience and goal.",
      },
      {
        title: "Select Channels",
        description:
          "Choose which channels to include: Direct Mail, Email, and/or Social Media. Set the campaign duration (3-12 weeks).",
      },
      {
        title: "Configure Channel Details",
        description:
          "For Direct Mail: select mail class, format, and quantity. The wizard calculates postage estimates automatically using current USPS rates.",
      },
      {
        title: "Set Up Targeting",
        description:
          "Configure audience segmentation, matching fund messaging, and donor premiums if applicable.",
      },
      {
        title: "Generate & Export",
        description:
          "Review AI-generated content for each channel. Export in your preferred format: Markdown, Google Docs, DOCX, or platform-specific formats.",
      },
    ],
    content: `The Campaign Builder wizard guides you through all steps. You can save drafts and return later - campaigns in progress appear in your Campaigns list.

For event-specific campaigns, the Event Campaign flow adds additional steps for venue, logistics, and run-of-show planning.`,
    tips: [
      "Use the Donor Data Lab first to identify your best segments before building a campaign",
      "The AI Personalization panel lets you create multiple content variants for different segments",
      "Export to Google Docs for easy collaboration with your team",
    ],
    relatedLinks: [
      ...(flags.enableCampaignWizard
        ? [{ label: "Start New Campaign", path: "build" }]
        : []),
      { label: "View All Campaigns", path: "campaigns" },
    ],
  },

  // =============================================================================
  // SEGMENTATION
  // =============================================================================
  {
    id: "segmentation",
    title: "How does segmentation work?",
    category: "features",
    summary:
      "Segmentation lets you send different versions of a campaign to different donor groups based on their behavior and giving history.",
    content: `Segmentation in Nexus is **behavioral** - it groups donors by their actions (giving recency, frequency, engagement) rather than demographics.

**Why segmentation matters:**
- A gratitude-focused message to active donors
- A reactivation appeal to LYBUNT donors (gave last year, not this year)
- A first-gift ask to engaged non-donors

Each group receives content that speaks directly to their relationship with your organization.

**Pre-built Segments:**
Nexus includes 14 ready-to-use segment templates:
- **Recency-based:** Active Donors, LYBUNT, SYBUNT, Long Lapsed
- **Giving-based:** Major Donors ($10k+), Mid-Level ($1k-$10k), Small Donors
- **Frequency-based:** Recurring Donors, Multi-Gift Donors, First-Time Donors
- **Engagement-based:** Upgrade Ready, Monthly Conversion Pool

**Custom Segments:**
Create your own segments using rules:
- Giving thresholds (lifetime, average, largest gift)
- Recency (days since last gift)
- Frequency (gift count, gifts per year)
- Behavioral flags (is monthly donor, upgrade ready, at risk)
- Tags (has any/all/missing specific tags)

**Privacy-Safe Design:**
Nexus stores only segment definitions (the rules), not donor lists. Counts and statistics are calculated on-demand and stored as aggregates only.`,
    tips: [
      "Start simple with 2-3 versions: All Donors, LYBUNT, and Non-Donors",
      "Segment priority matters - donors in multiple segments get content from the highest priority version",
      "Use the overlap visualization to understand how segments intersect",
    ],
    relatedLinks: [
      { label: "Manage Segments", path: "audiences" },
      { label: "Donor Data Lab", path: "donor-lab" },
    ],
  },

  // =============================================================================
  // DONOR DATA LAB
  // =============================================================================
  {
    id: "donor-lab",
    title: "How do I analyze my donor data?",
    category: "features",
    summary:
      "The Donor Data Lab analyzes your donor database to identify patterns, calculate segment statistics, and surface actionable insights.",
    requiredData: [
      "CSV or Excel file with donor records",
      "Recommended columns: Name/ID, donation amounts, donation dates",
      "Optional: Email, address, engagement metrics",
    ],
    steps: [
      {
        title: "Navigate to Donor Lab",
        description:
          "Open Donor Lab from the navigation rail under 'Improve & Configure'. Note: access requires appropriate permissions.",
        link: "donor-lab",
      },
      {
        title: "Upload Your Data",
        description:
          "Upload a CSV or Excel file containing donor records. The system will analyze columns and suggest mappings.",
      },
      {
        title: "Review Analysis",
        description:
          "View segment membership counts, giving statistics, and behavioral patterns. Identify upgrade prospects, monthly giving candidates, and reactivation targets.",
      },
      {
        title: "Launch Campaigns",
        description:
          "Jump directly to Campaign Builder with pre-selected segments based on your analysis.",
      },
    ],
    content: `The Donor Lab provides ephemeral analysis - donor-level data is processed during your session but only aggregate statistics are stored permanently.

**Analysis Outputs:**
- Segment membership counts
- Average and median giving by segment
- Behavioral scores (upgrade propensity, churn risk)
- Lookalike seed identification

**Privacy Model:**
- Donor IDs are visible only during upload session
- Only aggregated counts and statistics are permanently stored
- No donor-level data retention after session ends`,
    tips: [
      "Include at least 12 months of giving history for best segmentation results",
      "Run analysis before major campaigns to identify your best opportunities",
      "Use the 'Launch Campaign' button to pre-populate the Campaign Builder with your selected segments",
    ],
    relatedLinks: [
      { label: "Open Donor Lab", path: "donor-lab" },
      { label: "Segment Definitions", path: "audiences" },
    ],
  },

  // =============================================================================
  // EVENTS
  // =============================================================================
  {
    id: "events",
    title: "How do I create and manage events?",
    category: "features",
    summary:
      "Events are time-bound fundraising moments like galas, auctions, or giving days. The Event Wizard guides you through planning and generates a complete planning bundle.",
    requiredData: [
      "Event name, date, and location",
      "Event format (in-person, virtual, hybrid)",
      "Revenue goals and ticket pricing",
      "Sponsorship levels (if applicable)",
    ],
    steps: [
      {
        title: "Create New Event",
        description:
          "Navigate to Events and click '+ New Event' to open the Event Wizard.",
        link: "events",
      },
      {
        title: "Define Event Basics",
        description:
          "Enter event details: name, date, location, format, and description.",
      },
      {
        title: "Set Up Fundraising Structure",
        description:
          "Configure revenue streams: ticket sales, sponsorships, auction items, donations.",
      },
      {
        title: "Story & Theming",
        description:
          "Define the event's narrative and theme to guide communications and content.",
      },
      {
        title: "Generate Planning Bundle",
        description:
          "The wizard generates a comprehensive planning bundle including checklist, budget template, run of show, and communications plan.",
      },
    ],
    content: `**Planning Bundle Contents:**
- **Event Checklist:** 6 phases from 3-6 months out through post-event
- **Budget Template:** Revenue projections and expense tracking
- **Run of Show:** Detailed event timeline
- **Communications Plan:** Marketing timeline and messaging calendar

Events integrate with campaigns - you can create Event Campaigns that include pre-event promotion, day-of communications, and post-event follow-up.`,
    tips: [
      "Start planning at least 3 months out for major events",
      "Use the communications plan to coordinate multi-channel promotion",
      "Link your event to a campaign for integrated fundraising",
    ],
    relatedLinks: [
      { label: "View Events", path: "events" },
      { label: "Create New Event", path: "events/new" },
    ],
  },

  // =============================================================================
  // ACTIONABILITY (ADVANCED)
  // =============================================================================
  {
    id: "actionability",
    title: "How does Nexus determine what's actionable?",
    category: "advanced",
    summary:
      "The Actionability Framework evaluates insights based on magnitude, volume, persistence, and confidence to determine when recommendations are trustworthy.",
    content: `**Status Ladder:**
Every insight moves through a progression based on data quality:

1. **Interesting** - Pattern detected but not yet validated
2. **Monitor** - Worth watching, needs more data
3. **Actionable** - Ready for human decision-making
4. **Automatable** - High enough confidence for automated action

**Core Evaluation Criteria:**

| Metric | Threshold | Description |
|--------|-----------|-------------|
| Delta Magnitude | 15% | Minimum change from baseline to be meaningful |
| Data Volume | 100 records | Minimum sample size for reliable analysis |
| Persistence | 14 days | How long a pattern must hold to be trusted |
| Confidence | 70% | Minimum confidence score for actionability |

**Context Matters:**
Thresholds adjust based on:
- **Organization size** - Larger orgs need larger deltas (more noise)
- **Data maturity** - Newer data gets more lenient thresholds
- **Segment volatility** - Volatile segments need larger deltas
- **Historical variance** - Higher variance = stricter requirements

**Model vs. Observed:**
Nexus distinguishes between:
- **Observed facts:** Total raised, donor count, days remaining (no confidence labeling needed)
- **Model-derived:** Churn risk, propensity scores, LTV (always labeled with confidence)
- **Projections:** Forecasted revenue (always says "Projected" + confidence score)`,
    tips: [
      "Look for the Actionability Badge on insights to understand their status",
      "Click 'Why?' on any insight to see the full evaluation breakdown",
      "Higher-risk actions require higher confidence thresholds",
    ],
    relatedLinks: [
      { label: "View Overview", path: "" },
      { label: "Forecasting", path: "forecasting" },
    ],
  },

  // =============================================================================
  // FORECASTING
  // =============================================================================
  {
    id: "forecasting",
    title: "How do I use forecasting?",
    category: "features",
    summary:
      "Forecasting projects future revenue based on historical patterns, campaign performance, and donor behavior trends.",
    content: `The Forecasting page shows revenue projections across different time horizons:
- Monthly forecasts
- Quarterly rollups
- Year-end projections

**What powers forecasts:**
- Historical giving patterns by month/quarter
- Active campaign pacing and goals
- Donor retention and reactivation rates
- Seasonal adjustment factors

**Reading the forecast:**
- **Projected line:** Expected revenue based on current trajectory
- **Confidence band:** Range where actual results will likely fall
- **Pacing indicator:** Whether you're ahead/behind vs. plan

Forecasts are model-derived and always show confidence levels. They update as new data comes in.`,
    tips: [
      "Compare forecast to last year's actuals to validate projections",
      "Use forecast alerts to catch potential shortfalls early",
      "Forecasts become more accurate as you add historical data",
    ],
    relatedLinks: [
      { label: "View Forecasting", path: "forecasting" },
      { label: "Campaign Analytics", path: "campaigns" },
    ],
  },

  // =============================================================================
  // BRAND PROFILE
  // =============================================================================
  {
    id: "brand-profile",
    title: "How do I set up a brand profile?",
    category: "getting-started",
    summary:
      "The Brand Profile defines your organization's voice, messaging, and guidelines to power AI-generated content.",
    requiredData: [
      "Mission statement",
      "Vision statement",
      "Tone words (3-5 adjectives that describe your voice)",
      "Key messages and talking points",
      "Audience descriptions",
      "Prohibited language (words/phrases to avoid)",
      "Preferred language (words/phrases to use)",
    ],
    steps: [
      {
        title: "Navigate to Brand Profile",
        description:
          "Open Brand Profile from the navigation or Configuration > Brand tab.",
        link: "brand-profile",
      },
      {
        title: "Define Your Voice",
        description:
          "Enter mission, vision, and select tone words that describe how your organization communicates.",
      },
      {
        title: "Add Key Messages",
        description:
          "Document your core talking points and the messages you want to consistently reinforce.",
      },
      {
        title: "Set Language Guidelines",
        description:
          "Specify words and phrases to use or avoid. This helps AI-generated content match your style guide.",
      },
      {
        title: "Describe Your Audiences",
        description:
          "Define your primary audience segments so AI can tailor messaging appropriately.",
      },
    ],
    content: `A complete brand profile dramatically improves AI content quality. The more detail you provide, the more authentically the generated content will sound like your organization.

Brand profiles are shared across all campaigns for the client, ensuring consistent voice and messaging.`,
    tips: [
      "Include real examples of copy you love in the notes section",
      "Update the profile when messaging priorities change",
      "Use prohibited language to prevent common mistakes or off-brand terms",
    ],
    relatedLinks: [
      { label: "Edit Brand Profile", path: "brand-profile" },
      { label: "Configuration", path: "config?tab=brand" },
    ],
  },

  // =========================================================================
  // ROOM-FRAMED SECTIONS (Room → Surface → Action)
  // =========================================================================
  {
    id: "donors-portfolio",
    title: "Donors — Portfolio",
    category: "features",
    summary: "Your donor portfolio by tier, readiness, and story coverage.",
    content: `**What lives here:** Your donor portfolio organized by tier, readiness signals, and story coverage gaps.

**When do I come here:** When you need to understand who matters most, which donors are ready for outreach, and where your story gaps are.

**Key surfaces:**
- **Tier Overview** — Donors grouped by giving level with readiness indicators
- **Story Coverage tab** — Which donors have stories, which need them, and what type

**Where do I go next:** Stories → Worksheets to start capturing a story for a donor with a coverage gap.`,
    relatedLinks: [
      { label: "Open Portfolio", path: "major-donors" },
      { label: "Story Coverage", path: "major-donors?tab=story-coverage" },
      { label: "Start a Worksheet", path: "story-engine" },
    ],
  },
  {
    id: "stories-story-bank",
    title: "Stories — Story Bank",
    category: "features",
    summary: "Polished, deployable stories with quality intelligence.",
    content: `**What lives here:** Finished stories that have been promoted from Worksheets. Each story has quality scoring, ingredient analysis, and deployment status.

**When do I come here:** When you need to review, edit, or deploy a story. Also when checking overall story health across your portfolio.

**Key surfaces:**
- **Story list** — All stories with status (draft, ready, activated, deployed)
- **Story detail** — Full editor with the Intelligence panel showing quality scores
- **Filters** — By status, donor, campaign, or quality score

**Where do I go next:** Donors → Portfolio → Story Coverage tab to see which donors still need stories.`,
    relatedLinks: [
      { label: "Open Story Bank", path: "story-bank" },
      {
        label: "Story Coverage",
        path: "major-donors?tab=story-coverage",
      },
    ],
  },
  {
    id: "stories-worksheets",
    title: "Stories — Worksheets",
    category: "features",
    summary:
      "Story capture in progress — raw material being gathered and shaped.",
    content: `**What lives here:** Story worksheets where you capture raw material from donor interactions. Each worksheet uses the six-ingredient framework (character, setting, challenge, action, result, transformation).

**When do I come here:** When you're developing a story from a donor conversation, site visit, or program observation.

**Key surfaces:**
- **Worksheet list** — Active worksheets with completion progress
- **Worksheet editor** — Guided capture using the six-ingredient framework
- **Promote action** — When a worksheet is complete, promote it to the Story Bank

**Where do I go next:** Stories → Story Bank when the story is ready to polish and deploy.`,
    relatedLinks: [
      { label: "Open Worksheets", path: "story-engine" },
      { label: "Story Bank", path: "story-bank" },
    ],
  },
  {
    id: "meetings-meeting-prep",
    title: "Meetings — Meeting Prep",
    category: "features",
    summary:
      "Agendas and briefings for upcoming meetings, built from signals and task data.",
    content: `**What lives here:** Meeting preparation surfaces that pull together donor signals, recent activity, outstanding tasks, and suggested talking points into a single briefing.

**When do I come here:** Before a scheduled meeting — usually 24-48 hours ahead to review the agenda and briefing materials.

**Key surfaces:**
- **Meeting list** — Upcoming meetings with preparation status
- **Meeting briefing** — Auto-generated agenda with donor context, signals, and suggested actions
- **Post-meeting review** — After the meeting, capture outcomes and push actions to ClickUp

**Where do I go next:** After the meeting, review outcomes and push follow-up actions. For donor-specific deep dives, go to Donors → Portfolio.`,
    relatedLinks: [
      { label: "Open Meeting Prep", path: "meetings" },
      { label: "Donor Portfolio", path: "major-donors" },
    ],
  },
  {
    id: "configure-brand-profile",
    title: "Configure — Brand Profile",
    category: "getting-started",
    summary:
      "Your organization's voice, messaging, visual identity, and boilerplate.",
    content: `**What lives here:** The brand definition that powers all AI-generated content — mission, vision, tone words, key messages, prohibited language, and audience descriptions.

**When do I come here:** During initial setup to define your brand voice, or when messaging priorities change and the profile needs updating.

**Key surfaces:**
- **Voice & Tone** — Mission, vision, and tone word selection
- **Key Messages** — Core talking points and messaging pillars
- **Language Guidelines** — Words and phrases to use or avoid
- **Audience Descriptions** — Primary audience segments for content targeting

**Where do I go next:** Campaigns → Campaign Builder to see your brand voice applied to generated content.`,
    relatedLinks: [
      { label: "Edit Brand Profile", path: "brand-profile" },
      { label: "Content Quality", path: "config?tab=quality" },
    ],
  },
  {
    id: "configure-content-quality",
    title: "Configure — Content Quality",
    category: "features",
    summary:
      "Style rules, content standards, and uploaded reference materials.",
    content: `**What lives here:** Content quality configuration — style rules that govern AI output, content standards for review, and uploaded reference materials (annual reports, strategic plans, case statements).

**When do I come here:** When you need to upload a new report or reference document, or when quality guidelines need updating.

**Key surfaces:**
- **Style Rules** — Content standards that AI-generated text must follow
- **Reference Materials** — Upload zone for reports, plans, and documents the AI should reference
- **Quality Settings** — Thresholds and preferences for content review

**Where do I go next:** Stories → Story Bank to see quality scoring applied to your stories. Configure → Brand Profile to update voice settings.`,
    relatedLinks: [
      { label: "Content Quality Settings", path: "config?tab=quality" },
      { label: "Brand Profile", path: "brand-profile" },
      { label: "Story Bank", path: "story-bank" },
    ],
  },

  // =========================================================================
  // "WHERE DO I..." FAQ (searchable, Room → Surface → Action answers)
  // =========================================================================
  {
    id: "faq-upload-report",
    title: "Where do I upload a five year report?",
    category: "getting-started",
    summary:
      "Configure → Content Quality — upload reports and reference materials that the AI should draw from.",
    content: `Navigate to **Configure → Content Quality** and use the Reference Materials upload zone. Accepted formats include PDF, DOCX, and plain text. Uploaded documents are indexed and used as context for AI-generated content.`,
    relatedLinks: [{ label: "Content Quality", path: "config?tab=quality" }],
  },
  {
    id: "faq-brand-boilerplate",
    title: "Where does brand boilerplate live?",
    category: "getting-started",
    summary:
      "Configure → Brand Profile — mission, vision, key messages, and boilerplate text.",
    content: `Your brand boilerplate lives in **Configure → Brand Profile**. This includes mission statement, vision, key messages, and any standard language that should appear consistently across communications.`,
    relatedLinks: [{ label: "Brand Profile", path: "brand-profile" }],
  },
  {
    id: "faq-donors-need-stories",
    title: "Where do I see which donors need stories?",
    category: "getting-started",
    summary:
      "Donors → Portfolio → Story Coverage tab — shows donors with story gaps and suggested actions.",
    content: `Go to **Donors → Portfolio** and select the **Story Coverage** tab. This view shows which donors have stories, which have gaps, and what type of story would be most impactful. Donors with "uncaptured" gaps have a "Start Worksheet" action that takes you directly to Stories → Worksheets.`,
    relatedLinks: [
      { label: "Story Coverage", path: "major-donors?tab=story-coverage" },
      { label: "Start a Worksheet", path: "story-engine" },
    ],
  },
  {
    id: "faq-prepare-meeting",
    title: "Where do I prepare for a meeting?",
    category: "getting-started",
    summary:
      "Meetings → Meeting Prep — auto-generated briefings with donor context and suggested talking points.",
    content: `Navigate to **Meetings → Meeting Prep**. Select the upcoming meeting to see the auto-generated briefing, which includes donor signals, recent activity, outstanding tasks, and suggested talking points. Best reviewed 24-48 hours before the meeting.`,
    relatedLinks: [{ label: "Meeting Prep", path: "meetings" }],
  },
  {
    id: "faq-donor-segments",
    title: "Where do I see my donor segments?",
    category: "getting-started",
    summary:
      "Campaigns → Audiences — segment definitions, membership counts, and overlap analysis.",
    content: `Your donor segments live in **Campaigns → Audiences**. This shows all segment definitions (pre-built and custom), membership counts, and overlap visualization. You can also create new segments here.`,
    relatedLinks: [{ label: "Audiences", path: "audiences" }],
  },
  {
    id: "faq-campaign-performance",
    title: "Where do I check campaign performance?",
    category: "getting-started",
    summary:
      "Campaigns → select campaign → Analytics — performance metrics, pacing, and channel breakdowns.",
    content: `Go to **Campaigns**, select the campaign you want to review, and open the **Analytics** tab. This shows performance metrics, pacing against goals, and channel-level breakdowns.`,
    relatedLinks: [{ label: "Campaigns", path: "campaigns" }],
  },
  {
    id: "faq-meeting-schedule",
    title: "Where do I set up a new meeting schedule?",
    category: "getting-started",
    summary:
      "Configure → Settings → Integrations — connect calendar and configure meeting sync.",
    content: `Meeting schedules are configured in **Configure → Settings → Integrations**. Connect your calendar integration to sync upcoming meetings into Nexus, which then auto-generates meeting prep briefings.`,
    relatedLinks: [{ label: "Settings", path: "settings" }],
  },
  {
    id: "faq-gift-history",
    title: "Where do I see donor gift history?",
    category: "getting-started",
    summary:
      "Donors → Portfolio → click donor → Journey — full giving timeline and interaction history.",
    content: `Navigate to **Donors → Portfolio**, click on the donor you want to review, and open the **Journey** tab. This shows the full giving timeline, interaction history, and any linked stories or meeting notes.`,
    relatedLinks: [{ label: "Portfolio", path: "major-donors" }],
  },
  {
    id: "faq-edit-story",
    title: "Where do I edit a story?",
    category: "getting-started",
    summary:
      "Stories → Story Bank → click story — full editor with quality intelligence panel.",
    content: `Go to **Stories → Story Bank** and click on the story you want to edit. This opens the full story editor with the Intelligence panel showing quality scores, ingredient analysis, and deployment status.`,
    relatedLinks: [{ label: "Story Bank", path: "story-bank" }],
  },
  {
    id: "faq-story-quality",
    title: "Where do I track story quality?",
    category: "getting-started",
    summary:
      "Stories → Story Bank → click story → Intelligence panel — quality scores, ingredient coverage, readiness.",
    content: `Story quality is tracked in **Stories → Story Bank**. Click any story to see the **Intelligence panel**, which shows the overall quality score, six-ingredient coverage, and readiness classification. The Story Bank list view also shows quality indicators at a glance.`,
    relatedLinks: [{ label: "Story Bank", path: "story-bank" }],
  },

  // =========================================================================
  // ADVANCED
  // =========================================================================
  {
    id: "data-flow",
    title: "How data flows in Nexus",
    category: "advanced",
    summary:
      "The five-phase loop that powers Nexus behind the scenes: data in, patterns, lists out, outcomes, memory.",
    content: `Nexus operates on a five-phase data loop. Understanding this loop helps you trust how the system reaches its conclusions.

**1. Data comes in**
You import donor data as it arrives. Nexus records where it came from and when it changed.

**2. Patterns become visible**
Segments form based on real behavior: giving history, recency, and rules you control.

**3. Lists go out**
You export lists to act in the real world — mail, email, phone, whatever the channel. Nexus quietly remembers what left.

**4. Outcomes return**
When donations come back through imports, Nexus asks whether they might relate to something you sent.

**5. Memory accumulates**
Over time, Nexus builds a record of what you tried and what happened. No predictions. No scores. Just memory you can use next time.

This loop runs continuously. Each import strengthens the pattern recognition. Each export creates a measurement opportunity. The longer you use Nexus, the more useful its memory becomes.`,
    tips: [
      "You don't need to understand this loop to use Nexus — it's the engine, not the steering wheel",
      "The 6-room navigation model (Help → How Nexus Works) is the better starting point for day-to-day use",
    ],
    relatedLinks: [
      { label: "How Nexus Works (6 rooms)", path: "" },
      { label: "Actionability Framework", path: "" },
    ],
  },
];

/**
 * Get sections by category
 */
export function getSectionsByCategory(
  category: HelpSection["category"],
): HelpSection[] {
  return helpSections.filter((section) => section.category === category);
}

/**
 * Search sections by query (matches title, summary, and content)
 */
export function searchSections(query: string): HelpSection[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return helpSections;

  return helpSections.filter((section) => {
    const searchableText = [
      section.title,
      section.summary,
      section.content,
      ...(section.requiredData || []),
      ...(section.steps?.map((s) => s.title + " " + s.description) || []),
      ...(section.tips || []),
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(lowerQuery);
  });
}

/**
 * Category labels for display
 */
export const categoryLabels: Record<HelpSection["category"], string> = {
  "getting-started": "Getting Started",
  features: "Features",
  advanced: "Advanced",
};
